import type { TaskEvent, TaskEventKind, TaskEventSource } from './types'

const kinds = new Set<TaskEventKind>(['lifecycle', 'plan', 'diff', 'item', 'delta', 'reasoning', 'command', 'approval', 'tool', 'search', 'image', 'review', 'compaction', 'reroute', 'usage', 'warning', 'error', 'workflow', 'agent', 'browser', 'team', 'diagnostic'])
const sources = new Set<TaskEventSource>(['app-server', 'workflow'])
const rawReasoningKeys = new Set(['rawReasoning', 'raw_reasoning', 'reasoningContent', 'reasoningText', 'textDelta'])

function isRecord(value: unknown): value is Record<string, unknown> { return Boolean(value && typeof value === 'object' && !Array.isArray(value)) }
function hasRawReasoning(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(hasRawReasoning)
  if (!isRecord(value)) return false
  return Object.entries(value).some(([key, child]) => rawReasoningKeys.has(key) || hasRawReasoning(child))
}

export function validateTaskEvent(value: unknown): value is TaskEvent {
  if (!isRecord(value)) return false
  if (value.schemaVersion !== 1 || typeof value.eventId !== 'string' || value.eventId.length < 1 || value.eventId.length > 512) return false
  if (!Number.isInteger(value.sequence) || Number(value.sequence) < 0 || !Number.isFinite(value.timestamp)) return false
  if (typeof value.taskId !== 'string' || value.taskId.length < 1 || value.taskId.length > 512) return false
  if (!sources.has(value.source as TaskEventSource) || !kinds.has(value.kind as TaskEventKind) || !isRecord(value.payload)) return false
  for (const key of ['method', 'threadId', 'turnId', 'itemId', 'workflowId', 'projectId', 'cwd']) {
    if (value[key] !== undefined && (typeof value[key] !== 'string' || value[key].length > 1024)) return false
  }
  const item = value.payload.item
  const reasoningItem = Boolean(item && typeof item === 'object' && !Array.isArray(item) && (item as Record<string, unknown>).type === 'reasoning')
  if ((value.kind === 'reasoning' || reasoningItem) && hasRawReasoning(value.payload)) return false
  return true
}

export function diagnosticMessage(value: unknown): string { return value instanceof Error ? value.message.slice(0, 240) : 'Invalid task event' }
